// Admin > Feedback (page feedback, Connor 27 Oct 2026): the register of what
// people sent with right-click "Give feedback on this page…" (pageFeedback.ts,
// routers/page_feedback.py). An organisation admin sees their own
// organisation's; a platform admin sees every organisation's. Filters by face,
// status, workspace and free text; each row's status (new / seen / actioned /
// won't fix) is changed in place and audit-logged by the server.
// #feedback=<id> (the link on Connor's WRAP board) opens this tab on that item.
import { ApiError, getJson, patchJson } from "./api";
import { getState, roleAtLeast } from "./state";
import { showToast } from "./toast";
import { switchWorkspace } from "./workspaces";
import "./styles/pageFeedback.css";

interface FeedbackItem {
  id: number;
  client_id: number;
  client_name: string | null;
  user_id: number;
  username: string | null;
  display_name: string | null;
  created_at: string;
  score: number;
  face: string;
  text: string | null;
  workspace: string | null;
  route: string | null;
  project_id: string | null;
  package_id: string | null;
  build_id: string | null;
  element_selector: string | null;
  status: string;
  status_by_name: string | null;
  status_at: string | null;
  status_note: string | null;
  hub_review_id: number | null;
  hub_error: string | null;
  context: { where?: { workspace_label?: string; tab_label?: string; map?: { zoom?: number } | null }; element?: { text?: string } };
}

interface RegisterResponse {
  items: FeedbackItem[];
  all_orgs: boolean;
  statuses: string[];
  forwarding: boolean;
}

const EMOJI: Record<number, string> = { 1: "😞", 2: "🙁", 3: "😐", 4: "🙂", 5: "😄" };
const STATUS_LABEL: Record<string, string> = { new: "New", seen: "Seen", actioned: "Actioned", wontfix: "Won't fix" };

const filters = { score: "", status: "", workspace: "", search: "" };

export function focusedFeedbackId(): number | null {
  const m = /^#feedback=(\d+)/.exec(location.hash);
  return m ? Number(m[1]) : null;
}

function cell(text: string, className = ""): HTMLTableCellElement {
  const td = document.createElement("td");
  if (className) td.className = className;
  td.textContent = text;
  return td;
}

function select(options: [string, string][], value: string, onChange: (v: string) => void): HTMLSelectElement {
  const s = document.createElement("select");
  for (const [v, label] of options) {
    const o = document.createElement("option");
    o.value = v;
    o.textContent = label;
    s.append(o);
  }
  s.value = value;
  s.addEventListener("change", () => onChange(s.value));
  return s;
}

function labelled(label: string, control: HTMLElement): HTMLLabelElement {
  const l = document.createElement("label");
  l.append(label, control);
  return l;
}

export function renderFeedbackRegister(root: HTMLElement): void {
  root.innerHTML = "";
  const head = document.createElement("h3");
  head.textContent = "Page feedback";
  const intro = document.createElement("p");
  intro.className = "caption";
  intro.textContent = "What people sent with right-click “Give feedback on this page…” (or the Feedback link). Each item records the element, the page, the build, the map zoom and who sent it.";
  root.append(head, intro);

  const bar = document.createElement("div");
  bar.className = "fbr-filters";
  const reload = () => renderFeedbackRegister(root);
  bar.append(
    labelled("Face", select([["", "Any"], ["1,2", "Unhappy (1-2)"], ["3", "Neutral (3)"], ["4,5", "Happy (4-5)"]], filters.score, (v) => { filters.score = v; reload(); })),
    labelled("Status", select([["", "Any"], ["new", "New"], ["seen", "Seen"], ["actioned", "Actioned"], ["wontfix", "Won't fix"]], filters.status, (v) => { filters.status = v; reload(); })),
  );
  const ws = document.createElement("input");
  ws.placeholder = "e.g. delivery";
  ws.value = filters.workspace;
  const search = document.createElement("input");
  search.placeholder = "text, page or element";
  search.value = filters.search;
  const apply = document.createElement("button");
  apply.type = "button";
  apply.textContent = "Filter";
  apply.addEventListener("click", () => {
    filters.workspace = ws.value.trim();
    filters.search = search.value.trim();
    reload();
  });
  bar.append(labelled("Workspace", ws), labelled("Search", search), apply);
  root.append(bar);

  const status = document.createElement("p");
  status.className = "caption";
  status.textContent = "Loading feedback…";
  root.append(status);

  const focus = focusedFeedbackId();
  const q = new URLSearchParams();
  if (filters.score) q.set("score", filters.score);
  if (filters.status) q.set("status", filters.status);
  if (filters.workspace) q.set("workspace", filters.workspace);
  if (filters.search) q.set("search", filters.search);
  getJson<RegisterResponse>(`/api/feedback?${q}`)
    .then((res) => {
      status.textContent = `${res.items.length} item(s)${res.all_orgs ? " across every organisation" : ""}.` +
        (res.forwarding ? " New items are also filed on the WRAP board (without the written text)." : "");
      if (res.items.length === 0) return;
      root.append(table(res, focus));
    })
    .catch((e: unknown) => {
      status.textContent = e instanceof ApiError && e.status === 403 ? "Admins only." : "Could not load the feedback register.";
    });
}

function table(res: RegisterResponse, focus: number | null): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "admin-table-scroll";
  const t = document.createElement("table");
  t.className = "fbr-table";
  const hr = document.createElement("tr");
  for (const hd of ["Face", "When", "Who", ...(res.all_orgs ? ["Org"] : []), "Where", "Element", "Feedback", "Status"]) {
    const th = document.createElement("th");
    th.textContent = hd;
    hr.append(th);
  }
  t.append(hr);
  for (const it of res.items) {
    const tr = document.createElement("tr");
    tr.dataset.feedbackId = String(it.id);
    if (focus === it.id) tr.classList.add("fbr-focus");
    const face = cell(EMOJI[it.score] ?? String(it.score), "fbr-face");
    face.title = `${it.score}/5 ${it.face}`;
    face.setAttribute("aria-label", `${it.score} of 5: ${it.face}`);
    const when = cell(new Date(it.created_at).toLocaleString());
    if (it.build_id) when.append(Object.assign(document.createElement("div"), { className: "fbr-muted", textContent: `build ${it.build_id}` }));
    const who = cell(it.display_name ?? it.username ?? `user ${it.user_id}`);
    const w = it.context?.where;
    const where = cell([w?.workspace_label ?? it.workspace, w?.tab_label].filter(Boolean).join(" > ") || "—");
    const extra = [it.route, it.project_id && `project ${it.project_id}`, it.package_id && `package ${it.package_id}`,
      w?.map?.zoom != null ? `zoom ${w.map.zoom}` : ""].filter(Boolean).join(" · ");
    if (extra) where.append(Object.assign(document.createElement("div"), { className: "fbr-muted", textContent: extra }));
    const elCell = document.createElement("td");
    const code = document.createElement("code");
    code.textContent = it.element_selector ?? "the page";
    elCell.append(code);
    if (it.context?.element?.text) elCell.append(Object.assign(document.createElement("div"), { className: "fbr-muted", textContent: `"${it.context.element.text}"` }));
    const text = cell(it.text ?? "", "fbr-text");
    if (!it.text) text.append(Object.assign(document.createElement("span"), { className: "fbr-muted", textContent: "(no text)" }));
    const st = document.createElement("td");
    const sel = select(res.statuses.map((s) => [s, STATUS_LABEL[s] ?? s]), it.status, (v) => {
      sel.disabled = true;
      patchJson<FeedbackItem>(`/api/feedback/${it.id}`, { status: v })
        .then((u) => {
          it.status = u.status;
          sel.disabled = false;
          by.textContent = u.status_by_name ? `by ${u.status_by_name}` : "";
          showToast(`Feedback #${it.id}: ${STATUS_LABEL[u.status] ?? u.status}`);
        })
        .catch(() => {
          sel.value = it.status;
          sel.disabled = false;
          showToast("Could not change the status - try again.");
        });
    });
    sel.setAttribute("aria-label", `Status of feedback ${it.id}`);
    const by = Object.assign(document.createElement("div"), { className: "fbr-muted", textContent: it.status_by_name ? `by ${it.status_by_name}` : "" });
    st.append(sel, by);
    if (it.hub_review_id) st.append(Object.assign(document.createElement("div"), { className: "fbr-muted", textContent: `board #${it.hub_review_id}` }));
    else if (it.hub_error) st.append(Object.assign(document.createElement("div"), { className: "fbr-muted", textContent: "board: not filed yet (retrying)" }));
    tr.append(face, when, who);
    if (res.all_orgs) tr.append(cell(it.client_name ?? `org ${it.client_id}`));
    tr.append(where, elCell, text, st);
    t.append(tr);
  }
  wrap.append(t);
  if (focus !== null) setTimeout(() => t.querySelector(".fbr-focus")?.scrollIntoView({ block: "center" }), 0);
  return wrap;
}

/** main.ts, after the workspaces are applied: #feedback=<id> opens Admin > Feedback for an admin. */
export function openFeedbackDeepLink(): void {
  if (focusedFeedbackId() === null || !roleAtLeast(getState().currentUser, "admin")) return;
  if (!switchWorkspace("admin")) return;
  document.getElementById("admin-feedback-tab-btn")?.click();
}
